// Projects Data
// - priority: optional int (default 0), higher values appear first within featured/non-featured groups
// - Sorting order: featured first → higher priority → newer year

const projectsData = [
    {
        id: 'kisscam',
        title: 'KissCam',
        category: 'PRODUCTIVITY',
        description: 'Fast, minimal screen capture for macOS. Screenshots and video recording following the KISS principle: Keep It Simple, Screenshot.',
        fullDescription: `I take a lot of screenshots. I record my screen constantly. I just wanted something that captures what I need, saves it, and gets out of the way. No account, no subscription, no clutter.

KissCam lives in your menu bar and offers three capture modes (Full Screen, Window, Rectangle) for both screenshots and video. The standout feature is Quick Capture: hit a shortcut, get a file, no overlay needed. Perfect for repetitive workflows where you're capturing the same window or area over and over.

Multiple output formats, customizable shortcuts, audio options for recordings, and a clean native interface. Screen capture that stays out of your way.`,
        tags: ['Swift', 'macOS', 'Screenshot', 'Screen Recording', 'KISS'],
        imageSrc: '/assets/KissCam/kissCamIcon.png',
        featured: false,
        year: 2025,
        link: '/projects/kisscam',
    },
    {
        id: 'snobify',
        title: 'Snobify',
        category: 'OTHER',
        description: 'Break free from the music algorithm. Snobify is a macOS app for exploring new and meaningful methods of musical discovery.',
        fullDescription: `Snobify was born from a simple question: Are there better ways to discover music? We created this app as a platform for exploring new, exciting, and more meaningful methods of musical discovery. It's a powerful anti-algorithm for your Mac, built on the belief that true discovery happens when you break the cycle of popularity bias.`,
        tags: ['Swift', 'MacOs', 'Spotify', 'Data', 'Discovery', 'Music'],
        imageSrc: '/assets/projects/snobifyPreview.png',
        featured: false,
        year: 2025,
        link: '/projects/snobify',
    },
    {
        id: 'a-cat-game',
        title: 'A Cat Game', // Or final title if decided
        category: 'GAMING',
        // --- UPDATED description ---
        description: 'Explore a living painting in this 2D platformer. Control 3 unique cats with special abilities through dynamic, hand-painted worlds filled with charm and mystery.',
        // --- UPDATED fullDescription ---
        fullDescription: `Step into a playable painting. 'A Cat Game' features a unique aesthetic derived from real hand-painted artwork, brought to life with intricate 2D skeletal animation where nearly every asset reacts organically to the world.

Master the distinct movement styles and special abilities (double jump, gallop, dash) of three feline protagonists – Svetlana, Katya, and Bretzel. Chain actions for high-speed traversal and discover depth in frame-perfect mechanics.

Explore diverse environments, from forests under dynamic weather and day/night cycles to the moon itself, all filled with charm, mystery, and handcrafted detail. Follow Svetlana's journey after a strange event transforms her, pitting her against the nefarious Dr. Pouet Pouet in a quest to save cat-kind. Currently in active development using Unity.`,
        tags: ['Unity', 'C#', 'Game Design', '2D Art', 'Animation', 'Platformer'], // Added Platformer
        imageSrc: '/assets/projects/ACatGamePreview.png', // Make sure this path is correct
        gallery: [ /* Add paths to game screenshots/GIFs */ ],
        featured: true, // Changed to true as it's a major active project
        priority: 1,
        year: 2024, // Updated to current year or when active dev seriously started
        link: '/projects/a-cat-game', // Link to internal project page (teaser page)
    },
    {
        id: 'rso',
        title: 'RSO',
        category: 'AUDIO SOFTWARE',
        description: 'An instrument that responds rather than obeys. RSO combines exotic oscillators with familiar sculpting tools and a deep modulation system, built for dialogue, exploration, and happy accidents.',
        fullDescription: `RSO is a VST3 synthesizer built for dialogue. It responds to your input in ways that surprise you, pulling sounds in directions you wouldn't find alone. Whether you're designing for film, exploring new sonic territory, or just want a synth that feels alive under your fingers—RSO always brings something new to the session.

The sound engine pairs two unusual stereo oscillators—a Quaternion Phase Oscillator that thrives at the edge of chaos, and a harmonically dense Vector Phaseshaping engine—with filters you already know how to use: a Moog-style ladder, a West Coast wavefolder/LPG, and an Oberheim Xpander-inspired multimode. Strange sources, familiar shaping. Explore without losing control.

RSO comes alive through a deep and intuitive modulation system. Every parameter in the engine can be modulated, and modulators range from morphing LFOs to generative noise surveyors. Sounds breathe, evolve, never sit still. With MPE built in from the ground up, your performance translates directly into the modulation flow.

RSO is an instrument for discovery—yours to explore.`,
        tags: ['C++', 'JUCE', 'Audio DSP', 'VST3', 'MPE'],
        imageSrc: '/assets/RSO/img/heroShot.png',
        gallery: [],
        audioSamples: [],
        featured: true,
        priority: 10,
        year: 2024,
        link: '/projects/rso',
    },
    {
        id: 'faust-studio',
        title: 'Faust Studio',
        category: 'DEVELOPER TOOLS',
        description: 'Write, compile, and interact with Faust DSP code in real-time, directly within your JetBrains IDE.',
        fullDescription: `Transform your JetBrains IDE into a powerful, real-time audio development environment. Faust Studio removes the friction of traditional DSP development by bringing the full capabilities of the Faust functional programming language directly to your editor.

This plugin provides first-class language support for Faust, turning the IDE into a deeply intelligent environment. It features a custom inference engine for Arity Inlay Hints (visualizing signal flow), stub-based cross-file indexing for instant go-to-definition and rename refactoring across your entire project, and real-time inline diagnostics that catch errors as you type.

Coupled with a zero-setup WebAssembly audio engine, your code is instantly compiled and routed to your speakers. Verify your signal behavior immediately with a complete Oscilloscope featuring advanced triggering and CSV data export, alongside a scrolling Spectrogram and frequency Spectroscope.`,
        tags: ['Faust', 'Kotlin', 'TypeScript', 'IntelliJ', 'Plugin'],
        imageSrc: '/assets/FaustStudio/img/full.png',
        gallery: [],
        featured: true,
        priority: 9,
        year: 2026,
        link: '/projects/faust-studio',
    },
    {
        id: 'pukapok',
        title: 'PukaPok',
        category: 'PRODUCTIVITY',
        // --- IMPROVED description ---
        description: 'Rediscover your PDFs with PukaPok: a fast, modern library & viewer with smart organization, comfortable reading modes, and unique scientific reference linking.',
        // --- NEW fullDescription ---
        fullDescription: `Born from frustration with clunky, slow, and feature-poor PDF tools, PukaPok is a focused attempt to streamline how you manage and read documents. Experience fluid viewing with multi-phase rendering, advanced caching, true dark/sepia modes, and continuous scroll layouts.
    
Organize thousands of documents effortlessly using intuitive bookshelves, flexible tagging, and a built-in metadata editor. Find anything instantly with multi-phase search (exact, fuzzy, etc.) across all content and metadata, plus optional local AI-powered semantic search.
    
The standout feature is automatic hover-preview for scientific references (equations, figures, tables), eliminating tedious scrolling in research papers. Built with Python/Qt for speed and efficiency, PukaPok offers a clean interface for power users and researchers.`,
        tags: ['Python', 'Qt', 'UI/UX', 'PDF'],
        imageSrc: '/assets/projects/pukapokPreview.png', // Suggest using a specific preview image
        gallery: [ /* Add paths to PukaPok screenshots */ ],
        featured: true,
        year: 2025,
        link: '/projects/pukapok', // Link to internal project page
    },
    {
        id: 'adaptive-aesthetics',
        title: 'Adaptive ＡＥＳＴＨＥＴＩＣＳ',
        category: 'OTHER', // Changed category
        // --- IMPROVED description ---
        description: 'Elevate your IntelliJ IDE: Adaptive ＡＥＳＴＨＥＴＩＣＳ dynamically changes your background based on code analysis, from serene cosmic cats to demonic entities for errors.',
        // --- NEW fullDescription ---
        fullDescription: `Is your IDE emotionally dead? Does a null pointer exception fail to evoke existential dread? Adaptive ＡＥＳＴＨＥＴＩＣＳ solves this critical oversight by making your IDE *feel* your code's state.
    
Your background transforms: a serene surfing cat for clean code ✓, a screaming ethereal feline for warnings ⚠, and a terrifying multi-cat demon entity when syntax displeases the compiler gods ❌.
    
This isn't just cat pictures; it's essential developer infrastructure (proven* to boost error-fixing motivation by 73.5%). It fosters a deeper connection between you and your code. Available via GitLab releases. (*Metaphysical studies).`,
        tags: ['Kotlin', 'IntelliJ', 'Plugin', 'IDE', 'Aesthetics'], // Added IDE & Aesthetics
        imageSrc: '/assets/projects/AdaptiveAestheticsPreview.png', // Suggest specific preview
        gallery: [ /* Add paths to screenshots of the plugin in action */ ],
        featured: false, // Maybe feature this one? It's unique.
        year: 2025,
        link: '/projects/adaptive-aesthetics', // Link to internal project page
        // Optional: Add direct link to gitlab release page if needed elsewhere
        // externalLink: 'https://gitlab.com/lperron/adaptiveaesthetic/-/releases'
    },
    {
        id: 'almondo',
        title: 'AlmondO',
        category: 'AUDIO SOFTWARE',
        // --- IMPROVED description ---
        description: 'AlmondO: A complete tonewheel organ emulation running on an STM32F4 Discovery. Features 9 drawbars, leakage, vibrato, ADSR, and MIDI control via LUT wizardry.',
        // --- NEW fullDescription ---
        fullDescription: `How do you run 109 sine oscillators on a modest STM32F4 at 48kHz? AlmondO tackles this classic embedded audio challenge head-on, using no HAL libraries, just STM SPL code.
    
Instead of impossible calculations, it employs optimized Look-Up Tables (LUTs) for 12 base notes, with 109 'readers' accessing them at different speeds. It faithfully recreates the tonewheel sound with 9 MIDI-controllable drawbars, switchable harmonic leakage simulation, and essential delay-based vibrato.
    
Bonus features include a full ADSR envelope (for pads!) and a unique 'Glitchy Disto' effect. Fully functional on the STM32F4 Discovery board, it's a testament to optimizing for constrained hardware.`,
        tags: ['C++', 'ARM', 'Embedded', 'STM32', 'Audio DSP', 'DIY'], // Added STM32, Audio DSP, DIY
        imageSrc: '/assets/projects/almondoPreview.png', // Suggest specific preview
        gallery: [ /* Add paths to hardware pics, diagrams? */ ],
        featured: false,
        year: 2020, // Check year
        link: '/projects/almondo', // Link to internal project page
        // Optional: Add direct link to gitlab repo
        // externalLink: 'https://gitlab.com/lperron/almondo'
    },
    // --- Keep other projects like cats-game, mutated-rings, etc. ---
    // --- Make sure to update their `imageSrc`, `gallery`, `link` as needed ---
    {
        id: 'mutated-rings',
        title: 'Mutated Rings',
        category: 'AUDIO SOFTWARE',
        description: 'An alternative firmware for Mutable Instruments Rings, packing engines from Plaits and new ones like simulated Bell, 808 Cowbell, and Super VPS Oscillator.',
        fullDescription: `Expanding on the beloved Mutable Instruments Rings module, Mutated Rings is an alternative firmware that injects a wealth of new sonic possibilities.
    
It ports several popular algorithms from the Plaits macro-oscillator directly into the Rings architecture. Beyond ports, it introduces entirely new synthesis engines developed specifically for this project: a physically modeled Bell simulation, a classic 808 Cowbell emulation, and a simpler implementation of the powerful Super VPS (Vector Phaseshaping Synthesis) oscillator featured in the RSO VST.
    
This project involved deep diving into the original Rings and Plaits C++ codebases, understanding the STM32 hardware constraints, and implementing complex DSP algorithms efficiently in an embedded environment. It offers Eurorack users significantly more versatility from their Rings module.`,
        tags: ['C++', 'ARM', 'Embedded', 'Eurorack', 'Audio DSP', 'Firmware'], // Added Eurorack, Firmware
        imageSrc: '/assets/projects/mutatedRingsPreview.png', // Suggest specific preview
        gallery: [ /* Add paths to images/videos if available */ ],
        featured: false,
        year: 2020,
        link: '/projects/mutated-rings', // Link to internal project page
        // Optional: Add link to GitHub/GitLab if public
        externalLink: 'https://gitlab.com/lperron/mutated-rings'
    },
    // Add more projects as needed
];

export default projectsData;